import * as React from "react";
import { useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { ActionCard, DataList, Field, SelectField, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Button, ConfirmDialog, Section, StateView, formatDateTime, formatRelative, useAuth, useToast, When } from "@ozpanel/ui";

// THE SWITCH JOBS (internal/modules/storageswitch). Every move of a shared mountpoint between
// GlusterFS and JuiceFS, and every JuiceFS metadata-engine migration, is one row of
// shared_mount_jobs driven step by step by ONE node — the coordinator, the node that was allowed to
// run cluster singletons when it started. The JuiceFS engine card said "Progress is under Storage
// switch jobs" for as long as no such view existed.
//
// The server's rules, mirrored here rather than discovered as a 409:
//   - START (POST /storage/switch, POST /storage/juicefs/engine): only on the node allowed to run
//     cluster singletons — the declared primary — and one job at a time cluster-wide. The server
//     does not relay; it answers "ask that node".
//   - RESUME (POST /storage/switch/jobs/{id}/resume): only on the job's coordinator, and only while
//     the job is `running`. A failed job has already been rolled back — the answer is a new job. The
//     coordinator also resumes its own running jobs by itself at every converge pass after a restart,
//     so Resume is for a driver that has stopped; on a job its driver is still running it does nothing.
//     Nothing relays a resume to the coordinator either.

/** storageswitch.Job — job.go's json tags, exactly. */
interface SwitchJob {
  id: number;
  /** A path ("/mnt/cluster", "/home/<user>") — or "juicefs:<volume>" for a metadata-engine migration. */
  mountpoint: string;
  from: string;
  to: string;
  coordinator: string;
  state: string; // running | failed | done
  step: string;
  log: string[] | null;
  error?: string;
  /** The maintenance window (writers paused on every node), ms; absent until measured. */
  window_ms?: number;
  created_at: string;
  updated_at: string;
}

// The step orders are the server's (job.go `steps`, enginejob.go `engineSteps`), copied so the detail
// can show where a job is among them. The job's own `step` is what is authoritative.
const pathSteps = ["preflight", "mark", "bulk_copy", "preverify", "quiesce", "delta_copy", "verify", "swap", "resume", "done"];
const engineSteps = ["preflight", "hold", "quiesce", "unmount", "dump", "reset", "load", "verify", "set_engine", "mount", "resume", "done"];
const enginePrefix = "juicefs:"; // enginejob.go engineJobPrefix

const isEngine = (j: SwitchJob) => j.mountpoint.startsWith(enginePrefix);
const subject = (j: SwitchJob) => (isEngine(j) ? j.mountpoint.slice(enginePrefix.length) : j.mountpoint);
const stateTone = (s: string) => (s === "done" ? "ok" : s === "running" ? "accent" : s === "failed" ? "bad" : "neutral");

/** The two facts the start/resume rules turn on, from /cluster/state (same query the Cluster page uses). */
function useWhoCoordinates() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["cluster-state"],
    queryFn: () => client.apiFetch<{ state: { self: string; primary: string } }>("/cluster/state"),
    refetchInterval: 15000,
  });
  return { self: q.data?.state?.self, primary: q.data?.state?.primary, error: q.error };
}

function windowText(ms?: number) {
  if (!ms) return null;
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}

export function StorageSwitchJobs({ openJob, onOpenJob }: { openJob: number | null; onOpenJob: (id: number | null) => void }) {
  const { client } = useAuth();
  const who = useWhoCoordinates();
  const q = useQuery({
    queryKey: ["storage-switch-jobs"],
    queryFn: () => client.apiFetch<SwitchJob[] | null>("/storage/switch/jobs"),
    // Fast while anything is moving, slow otherwise.
    refetchInterval: (query) => ((query.state.data ?? []).some((j) => j.state === "running") ? 4000 : 30000),
  });
  const jobs = q.data ?? [];
  const running = jobs.filter((j) => j.state === "running");
  // The start form's fields live here so the GlusterFS way-back steps can prefill them: ONE form starts
  // every path switch, whichever panel the operator came from.
  const [mp, setMp] = React.useState("");
  const [to, setTo] = React.useState("juicefs");
  const formRef = React.useRef<HTMLDivElement>(null);
  const ret = useRetirement();
  const vols = useGlusterVolumes(ret.data);
  const switchBack = (path: string) => {
    setMp(path);
    setTo("glusterfs");
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const columns: DataListColumn<SwitchJob>[] = [
    { key: "id", label: "Job", sortable: true, value: (j) => j.id, render: (j) => <span className="font-mono text-[12px]">#{j.id}</span> },
    {
      key: "kind",
      label: "What moves",
      sortable: true,
      value: (j) => subject(j),
      render: (j) => (
        <span>
          <span className="font-mono text-[12px]">{subject(j)}</span>{" "}
          <span className="text-[11.5px] text-muted">{isEngine(j) ? "metadata engine" : "mountpoint"}</span>
        </span>
      ),
    },
    {
      key: "move",
      label: "From → to",
      render: (j) => (
        <span className="font-mono text-[12px]">
          {j.from || "unknown"} → {j.to || "unknown"}
        </span>
      ),
    },
    { key: "state", label: "State", sortable: true, render: (j) => <StatusPill tone={stateTone(j.state)}>{j.state}</StatusPill> },
    { key: "step", label: "Step", sortable: true, render: (j) => <span className="font-mono text-[12px]">{j.step || "unknown"}</span> },
    {
      key: "coordinator",
      label: "Coordinator",
      sortable: true,
      render: (j) => (
        <span className="font-mono text-[12px]">
          {j.coordinator}
          {who.self && j.coordinator === who.self && <span className="ml-1 text-muted">(this node)</span>}
        </span>
      ),
    },
    { key: "created_at", label: "Started", sortable: true, render: (j) => (j.created_at ? formatDateTime(j.created_at) : "unknown") },
    {
      key: "updated_at",
      label: "Finished",
      sortable: true,
      // A running job has no finish; its last update is shown instead, because a running job whose
      // last update is old is a stopped driver, and that is the case Resume exists for.
      render: (j) =>
        j.state === "running" ? (
          <span className="text-muted">running · updated {formatRelative(j.updated_at)}</span>
        ) : j.updated_at ? (
          formatDateTime(j.updated_at)
        ) : (
          "unknown"
        ),
    },
  ];

  return (
    <div className="space-y-4">
      <GlusterRetirement self={who.self} primary={who.primary} jobs={jobs} jobsKnown={!!q.data} ret={ret} vols={vols} onSwitchBack={switchBack} />
      <Section
        title="Storage switch jobs"
        hint={
          q.data
            ? running.length > 0
              ? `${running.length} running — one at a time, cluster-wide`
              : `${jobs.length} job(s), the 50 most recent; none running`
            : undefined
        }
        bodyClassName="p-0"
      >
        <div className="border-b border-line px-4 py-2.5 text-[12px] leading-relaxed text-muted">
          Every move of a shared path between GlusterFS and JuiceFS, and every JuiceFS metadata-engine migration (started
          from the engine card on the JuiceFS &amp; Garage tab). Each is driven by one node, its coordinator; writers pause
          on every node for the final copy and the swap, and the pause is measured and shown as the window. A failure after
          the pause puts everything back; the old backend's copy is kept either way.
        </div>
        <div className="p-4">
          <DataList
            items={jobs}
            columns={columns}
            getRowKey={(j) => String(j.id)}
            loading={q.isLoading}
            error={q.error}
            errorContext="the switch jobs"
            expandedKey={openJob == null ? null : String(openJob)}
            renderExpanded={(j: SwitchJob) => <JobDetail id={j.id} self={who.self} />}
            rowActions={(j: SwitchJob) => (
              <div className="flex justify-end">
                <Button size="sm" variant="ghost" onClick={() => onOpenJob(openJob === j.id ? null : j.id)}>
                  {openJob === j.id ? "Close" : "Details"}
                </Button>
              </div>
            )}
            emptyMessage="No switch job has ever run on this cluster."
          />
        </div>
      </Section>
      <div ref={formRef}>
        <StartSwitch
          self={who.self}
          primary={who.primary}
          running={running}
          known={!!q.data}
          mp={mp}
          setMp={setMp}
          to={to}
          setTo={setTo}
          ret={ret}
          vols={vols}
        />
      </div>
    </div>
  );
}

function JobDetail({ id, self }: { id: number; self?: string }) {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const [asking, setAsking] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const q = useQuery({
    queryKey: ["storage-switch-job", id],
    queryFn: () => client.apiFetch<SwitchJob>(`/storage/switch/jobs/${id}`),
    refetchInterval: (query) => (query.state.data?.state === "running" ? 3000 : false),
    retry: false,
  });
  const j = q.data;
  const logRef = React.useRef<HTMLPreElement>(null);
  const logLen = j?.log?.length ?? 0;
  React.useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logLen]);

  const order = j ? (isEngine(j) ? engineSteps : pathSteps) : [];
  const at = j ? order.indexOf(j.step) : -1;
  // handleResume's two refusals, in its order: not the coordinator, then not running.
  const resumeBlocked = !j
    ? "The job has not been read."
    : !self
      ? "Which node this console is on could not be read, so whether it coordinates this job is unknown."
      : j.coordinator !== self
        ? `Coordinated by ${j.coordinator}: only that node can resume it, and nothing relays the request — open ${j.coordinator}'s console.`
        : j.state !== "running"
          ? j.state === "failed"
            ? "This job failed and was rolled back — the path is as it was. Start a new job instead."
            : `This job is ${j.state}; only a running job resumes.`
          : null;

  return (
    <StateView loading={q.isLoading} error={q.error} errorContext={`switch job #${id}`}>
      {j && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {order.map((s, i) => {
              const tone =
                j.state === "done" || i < at ? "ok" : i === at ? (j.state === "failed" ? "bad" : "accent") : "neutral";
              return (
                <StatusPill key={s} tone={tone}>
                  {s}
                </StatusPill>
              );
            })}
            {at < 0 && <span className="text-[12px] text-warn">recorded step “{j.step}” is not one this console knows</span>}
          </div>
          <div className="grid gap-x-6 gap-y-1 text-[12.5px] md:grid-cols-2">
            <div>
              <span className="text-muted">Window (writers paused): </span>
              {windowText(j.window_ms) ?? <span className="text-muted">not measured yet</span>}
            </div>
            <div>
              <span className="text-muted">Last update: </span>
              <When value={j.updated_at} />
              {j.state === "running" && <span className="text-muted"> · refreshing every 3 s</span>}
            </div>
          </div>
          {j.error && <div className="rounded border border-line bg-critsoft px-3 py-2 text-[12px] leading-relaxed text-crit">{j.error}</div>}
          <div>
            <div className="mb-1 text-[11px] font-medium tracking-wide text-muted uppercase">Log (times UTC)</div>
            {j.log && j.log.length > 0 ? (
              <pre ref={logRef} className="max-h-72 overflow-auto rounded-lg border border-line bg-bg px-3 py-2 font-mono text-[11px] whitespace-pre-wrap">
                {j.log.join("\n")}
              </pre>
            ) : (
              <p className="text-[12px] text-muted">No log lines recorded yet.</p>
            )}
          </div>
          <div className="flex items-center justify-end gap-3">
            {resumeBlocked && <span className="text-[12px] text-muted">{resumeBlocked}</span>}
            <Button size="sm" variant="default" disabled={!!resumeBlocked || busy} onClick={() => setAsking(true)}>
              {busy ? "Resuming…" : "Resume"}
            </Button>
          </div>
          <ConfirmDialog
            open={asking}
            onOpenChange={setAsking}
            title={`Resume switch job #${j.id} from “${j.step}”?`}
            confirmLabel="Resume"
            destructive
            pending={busy}
            body={
              <p className="text-[12.5px] leading-relaxed">
                The job continues from its recorded step; every step is idempotent. If this node is still driving it, this
                does nothing. Steps from <span className="font-mono">quiesce</span> onward pause mail, web, apps and cron on
                every node until the job resumes them or rolls back.
              </p>
            }
            onConfirm={() => {
              setAsking(false);
              setBusy(true);
              void client
                .apiFetch<SwitchJob>(`/storage/switch/jobs/${j.id}/resume`, { method: "POST" })
                .then(() => {
                  notify({ kind: "success", title: `Switch job #${j.id} resumed` });
                  void qc.invalidateQueries({ queryKey: ["storage-switch-job", j.id] });
                  void qc.invalidateQueries({ queryKey: ["storage-switch-jobs"] });
                })
                .catch((e: unknown) => notify({ kind: "error", title: `Job #${j.id} was not resumed`, body: e instanceof Error ? e.message : String(e) }))
                .finally(() => setBusy(false));
            }}
          />
        </div>
      )}
    </StateView>
  );
}

/** What GET /storage/glusterfs/retirement answers (retire.go RetirementStatus), key for key. */
interface Retirement {
  node: string;
  /** This node's marker (sharedfs.GlusterRetiredMarker) — what THIS node has applied. */
  applied_here: boolean;
  /** The cluster-wide intent: a bool, or "unreadable: …". Unset reads as retired (a cluster built since
   *  2026-10-06 never had GlusterFS); only an explicit un-retire makes it false. */
  intent: boolean | string;
  /** glusterd's state here; null with glusterd_running_error when it could not be asked. Absent when the
   *  glusterfs module is not wired on this node. */
  glusterd_running_here?: boolean | null;
  glusterd_running_error?: string;
  /** Only when NOT retired and asked on the coordinator: every volume → the mountpoint it serves, and
   *  what blocks retiring (each node's KERNEL asked about each mountpoint). */
  volumes?: Record<string, string> | null;
  blockers?: string[] | null;
  /** The coordinator's preflight could not run — why. */
  preflight?: string;
}
type RetirementQuery = UseQueryResult<Retirement>;

/** glusterfs/volumes.go Volume — the fields read here. */
interface GlusterVolume {
  name: string;
  status?: string;
  started?: boolean;
  brick_count?: number;
  bricks_online?: number;
  status_read?: boolean;
}
type VolumesQuery = UseQueryResult<{ items: GlusterVolume[] | null; total?: number }>;

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** storageswitch node.go glusterVolumeFor: the volume that serves a switchable path. */
function glusterVolumeFor(path: string) {
  return path === "/mnt/cluster" ? "cluster" : "oz_" + path.replace(/^\/home\//, "");
}
/** glusterfs mountsfromvolumes.go mountPointForVolume, for the two names the switch job knows. */
function mountpointForVolume(vol: string) {
  if (vol === "cluster") return "/mnt/cluster";
  const u = vol.startsWith("oz_") ? vol.slice(3) : "";
  return u && !/[/. ]/.test(u) ? "/home/" + u : "";
}

function useRetirement(): RetirementQuery {
  const { client } = useAuth();
  return useQuery({
    queryKey: ["glusterfs-retirement"],
    queryFn: () => client.apiFetch<Retirement>("/storage/glusterfs/retirement"),
    refetchInterval: 15000,
    retry: false,
  });
}

/** The volume list answers only where glusterd runs — so it is asked only once this node is un-retired. */
function useGlusterVolumes(ret?: Retirement): VolumesQuery {
  const { client } = useAuth();
  return useQuery({
    queryKey: ["gluster-volumes"],
    queryFn: () => client.apiFetch<{ items: GlusterVolume[] | null; total?: number }>("/glusterfs/volumes"),
    enabled: ret?.intent === false && ret.applied_here === false,
    refetchInterval: 15000,
    retry: false,
  });
}

// GLUSTERFS RETIREMENT — and the way back (retire.go).
//
// The server's rules, mirrored rather than discovered as a 409:
//   - RETIRE (POST /storage/glusterfs/retire): only the node allowed to run cluster singletons (the
//     declared primary — the same coordinator as a switch), only while no switch job runs, and only when
//     every gluster volume's mountpoint is served by JuiceFS on EVERY node, asked of each kernel. Those
//     blockers are computed by GET …/retirement only on the coordinator and only while not retired, so
//     anywhere else this console cannot list them and says so.
//   - UN-RETIRE (DELETE /storage/glusterfs/retire): no gate at all — any node, any time, even while a
//     switch runs. It writes the intent and applies it on THIS node; every other node follows on its
//     own pass. The volumes stay STOPPED, and no path moves: a path goes back only through a switch job.
//   - Only THIS node's applied state is reported. Nothing answers for the other nodes' markers or
//     glusterd; their own consoles do.
function GlusterRetirement({
  self,
  primary,
  jobs,
  jobsKnown,
  ret,
  vols,
  onSwitchBack,
}: {
  self?: string;
  primary?: string;
  jobs: SwitchJob[];
  jobsKnown: boolean;
  ret: RetirementQuery;
  vols: VolumesQuery;
  onSwitchBack: (path: string) => void;
}) {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const [starting, setStarting] = React.useState<string | null>(null);
  const r = ret.data;
  const intent = r?.intent;
  const running = jobs.filter((j) => j.state === "running");
  const retired = intent === true;
  const unretired = intent === false;

  const glusterd =
    r?.glusterd_running_here === true ? (
      <StatusPill tone={retired ? "bad" : "ok"}>running here</StatusPill>
    ) : r?.glusterd_running_here === false ? (
      <StatusPill tone={retired ? "ok" : "warn"}>stopped here</StatusPill>
    ) : r && "glusterd_running_here" in r ? (
      <StatusPill tone="warn">unknown here: {r.glusterd_running_error ?? "not asked"}</StatusPill>
    ) : (
      <StatusPill tone="neutral">not reported (no glusterfs module here)</StatusPill>
    );
  // The intent and THIS node's marker disagree until this node's pass applies it.
  const lag = r && typeof intent === "boolean" && intent !== r.applied_here;

  const retireUnavailable = !r
    ? "The retirement state has not been read."
    : typeof intent === "string"
      ? `The intent could not be read (${intent}), so nothing can be said about retiring.`
      : retired
        ? "GlusterFS is already retired."
        : !jobsKnown
          ? "The switch jobs have not been read, so whether one is running is unknown."
          : running.length > 0
            ? `Switch job ${running.map((j) => "#" + j.id).join(", ")} is still running — retirement waits for it.`
            : !self || !primary
              ? "Which node coordinates (the declared primary) could not be read from the cluster state."
              : self !== primary
                ? `Only the node allowed to run cluster singletons retires GlusterFS — the declared primary, ${primary}. This console is on ${self}, and nothing relays: open ${primary}'s console, where the blockers are also listed.`
                : r.preflight
                  ? `The coordinator's check could not run: ${r.preflight}`
                  : r.blockers && r.blockers.length > 0
                    ? (
                        <span>
                          GlusterFS still serves something — the server refuses until each is gone:
                          <ul className="mt-1 list-inside list-disc">
                            {r.blockers.map((b) => (
                              <li key={b} className="font-mono text-[11.5px]">
                                {b}
                              </li>
                            ))}
                          </ul>
                        </span>
                      )
                    : !r.volumes
                      ? "The server did not report its preflight, so whether anything blocks retiring is unknown."
                      : undefined;
  const volNames = Object.keys(r?.volumes ?? {}).sort();

  const unretireUnavailable = !r
    ? "The retirement state has not been read."
    : typeof intent === "string"
      ? `The intent could not be read (${intent}).`
      : unretired
        ? "GlusterFS is not retired."
        : undefined;

  // THE WAY BACK, as measured steps. Step 2 and 3 read the volume list, which only glusterd answers.
  const volItems = vols.data?.items ?? [];
  const lastJob = (path: string) => jobs.filter((j) => j.mountpoint === path).sort((a, b) => b.id - a.id)[0];
  const step1 = unretired ? (r?.applied_here === false ? "done" : "applying") : retired ? "todo" : "unknown";

  return (
    <Section
      title="GlusterFS retirement"
      hint={
        retired
          ? "Retired cluster-wide — every shared path is on JuiceFS; bricks kept on disk"
          : unretired
            ? "Not retired — glusterd runs; every path stays where its switch jobs put it"
            : undefined
      }
      actions={
        r ? (
          <StatusPill tone={typeof intent === "string" ? "warn" : retired ? "neutral" : "accent"}>
            {typeof intent === "string" ? "intent unreadable" : retired ? "retired" : "not retired"}
          </StatusPill>
        ) : undefined
      }
    >
      <StateView loading={ret.isLoading} error={ret.error} errorContext="the GlusterFS retirement state">
        {r && (
          <div className="space-y-4">
            <div className="grid gap-x-6 gap-y-1.5 text-[12.5px] md:grid-cols-3">
              <div>
                <span className="text-muted">Intent (cluster-wide): </span>
                {typeof intent === "string" ? <span className="text-warn">{intent}</span> : retired ? "retired" : "not retired"}
              </div>
              <div>
                <span className="text-muted">Applied on {r.node} (this node): </span>
                {r.applied_here ? "retired marker present" : "no retired marker"}
                {lag && <span className="text-warn"> — not yet applied here; this node's next pass does it</span>}
              </div>
              <div>
                <span className="text-muted">glusterd: </span>
                {glusterd}
              </div>
            </div>
            <p className="text-[11.5px] leading-relaxed text-muted">
              Only this node's applied state is reported. Every other node applies the intent on its own pass and reports only on
              its own console. On a retired node a glusterd found running is stopped again and reported — something started it.
            </p>

            <ActionCard
              title="Retire GlusterFS"
              description={
                <span>
                  Stops every GlusterFS volume and records the retirement; every node then stops and disables glusterd.
                  {volNames.length > 0 && (
                    <>
                      {" "}
                      Volumes it would stop:{" "}
                      <span className="font-mono">{volNames.map((v) => `${v} (${r.volumes?.[v] || "no known mountpoint"})`).join(", ")}</span>.
                    </>
                  )}
                </span>
              }
              actionLabel="Retire"
              danger="high"
              unavailable={retireUnavailable}
              confirm={{
                title: "Retire GlusterFS on every node?",
                confirmLabel: "Retire",
                body: (
                  <div className="space-y-2 text-[12.5px] leading-relaxed">
                    <p>
                      <b>On {self} (the coordinator):</b> checks again that no switch is running and that every node's kernel serves every
                      volume's mountpoint from JuiceFS; stops every volume ({volNames.join(", ") || "none listed"}) — a cluster-wide{" "}
                      <span className="font-mono">gluster volume stop</span>; records the intent; then retires this node: removes any GlusterFS
                      fstab line for a path JuiceFS serves, unlinks those mount units from glusterd (stopping glusterd with that link in place
                      once unmounted every JuiceFS home), writes the retired marker, stops and disables glusterd.
                    </p>
                    <p>
                      <b>On every other node:</b> the same per-node retirement on its next pass. From the marker on, every automatic GlusterFS
                      repair on that node stands down.
                    </p>
                    <p>
                      <b>Kept:</b> the bricks, on disk. If a volume will not stop, the retirement is not recorded and the volumes already
                      stopped stay stopped — the server says which.
                    </p>
                  </div>
                ),
              }}
              run={() => client.apiFetch<Record<string, unknown>>("/storage/glusterfs/retire", { method: "POST" })}
              invalidates={[["glusterfs-retirement"], ["gluster-volumes"]]}
              renderResult={(x) => <RetireResult value={x as Record<string, unknown>} />}
            />

            <ActionCard
              title="Un-retire GlusterFS"
              description="Clears the retirement: glusterd is enabled and started on this node now, and on every other node at its next pass. The volumes stay stopped and no path moves — every path stays on JuiceFS until a switch job moves it back."
              actionLabel="Un-retire"
              danger="medium"
              unavailable={unretireUnavailable}
              preflight={
                running.length > 0
                  ? [{ ok: false, label: `Switch job ${running.map((j) => "#" + j.id).join(", ")} is running`, detail: "the server does not wait for it" }]
                  : undefined
              }
              confirm={{
                title: "Un-retire GlusterFS on every node?",
                confirmLabel: "Un-retire",
                body: (
                  <div className="space-y-2 text-[12.5px] leading-relaxed">
                    <p>
                      <b>On this node{self ? ` (${self})` : ""}:</b> records the intent as not retired, removes the retired marker, enables
                      and starts glusterd. Any node may do this — the server has no coordinator rule for it.
                    </p>
                    <p>
                      <b>On every other node:</b> the same on its next pass. With the marker gone, the automatic GlusterFS repairs run there
                      again.
                    </p>
                    <p>
                      <b>Not done:</b> no volume is started (each stays stopped until started — step 2 below), nothing is mounted from
                      GlusterFS, and no data moves. Moving a path back is a separate switch job per path (step 3).
                    </p>
                  </div>
                ),
              }}
              run={() => client.apiFetch<Record<string, unknown>>("/storage/glusterfs/retire", { method: "DELETE" })}
              invalidates={[["glusterfs-retirement"], ["gluster-volumes"]]}
              renderResult={(x) => <RetireResult value={x as Record<string, unknown>} />}
            />

            {/* POST /glusterfs/bricks/empty-retired (glusterfs/emptybricks.go): frees the disk the retired
                bricks hold on THIS node. The server judges every brick before touching any and refuses unless
                this node is retired, glusterd is stopped and each brick's mountpoint is served by JuiceFS; the
                card mirrors the first two so the refusal is not the first place they are learned. */}
            <ActionCard
              title={`Empty the retired bricks on ${r.node}`}
              description="Deletes the stale data under every brick glusterd's store lists for this node, and keeps each brick root with its volume-id and gfid attributes — so a switch back to GlusterFS (which copies the live JuiceFS tree into the volume) still starts. Frees the disk the retired copies hold. This node only: run it on each node."
              actionLabel="Empty bricks"
              danger="high"
              unavailable={
                !r.applied_here
                  ? `GlusterFS is not retired on ${r.node} (no retired marker), so the server refuses.`
                  : r.glusterd_running_here === true
                    ? `glusterd is running on ${r.node}, so the server refuses.`
                    : undefined
              }
              confirm={{
                title: `Empty every retired brick on ${r.node}?`,
                confirmLabel: "Empty bricks",
                body: (
                  <p className="text-[12.5px] leading-relaxed">
                    The data under each brick root is deleted for good; the roots and their attributes stay. The live files are on
                    JuiceFS and are not touched — the server refuses if any brick's mountpoint is not served by JuiceFS here.
                  </p>
                ),
              }}
              run={() => client.apiFetch<Record<string, unknown>>("/glusterfs/bricks/empty-retired", { method: "POST" })}
              invalidates={[["glusterfs-retirement"]]}
            />

            <div>
              <div className="mb-1.5 text-[11px] font-medium tracking-wide text-muted uppercase">The way back to GlusterFS</div>
              <ol className="space-y-2 text-[12.5px]">
                <li className="flex flex-wrap items-baseline gap-2">
                  <StatusPill tone={step1 === "done" ? "ok" : step1 === "applying" ? "warn" : "neutral"}>1</StatusPill>
                  <span>Un-retire GlusterFS</span>
                  <span className="text-muted">
                    {step1 === "done"
                      ? `done — intent not retired, ${r.node} has applied it`
                      : step1 === "applying"
                        ? `intent not retired; ${r.node} has not applied it yet`
                        : step1 === "todo"
                          ? "not done — GlusterFS is retired"
                          : "unknown — the intent could not be read"}
                    {" "}· other nodes: on their own consoles
                  </span>
                </li>
                <li>
                  <div className="flex flex-wrap items-baseline gap-2">
                    <StatusPill tone={volItems.length > 0 && volItems.every((v) => v.started) ? "ok" : "neutral"}>2</StatusPill>
                    <span>Start each volume to move back</span>
                    <span className="text-muted">
                      {step1 !== "done"
                        ? "after step 1 — glusterd lists the volumes only once it runs here"
                        : vols.isLoading
                          ? "reading the volumes…"
                          : vols.error
                            ? "the volumes could not be listed: " + errText(vols.error)
                            : volItems.length === 0
                              ? "no volumes on this cluster — nothing to move back to"
                              : `${volItems.filter((v) => v.started).length} of ${volItems.length} started`}
                    </span>
                  </div>
                  {step1 === "done" && volItems.length > 0 && (
                    <table className="mt-1.5 w-full max-w-3xl text-[12px]">
                      <tbody>
                        {volItems.map((v) => {
                          const path = mountpointForVolume(v.name);
                          const j = path ? lastJob(path) : undefined;
                          const backBlocked = !path
                            ? "not a path the switch job moves"
                            : !v.started
                              ? "start the volume first"
                              : running.length > 0
                                ? "a switch job is running — one at a time"
                                : j && j.state === "done" && j.to === "glusterfs"
                                  ? `job #${j.id} already moved it back to GlusterFS`
                                  : undefined;
                          return (
                            <tr key={v.name} className="border-t border-line">
                              <td className="py-1 pr-3 font-mono">{v.name}</td>
                              <td className="py-1 pr-3 font-mono">{path || "—"}</td>
                              <td className="py-1 pr-3">
                                <StatusPill tone={v.started ? "ok" : "bad"}>{v.started ? "started" : v.status || "stopped"}</StatusPill>
                                {v.started && v.status_read && (
                                  <span className="ml-1 text-muted">
                                    {v.bricks_online ?? 0}/{v.brick_count ?? 0} bricks
                                  </span>
                                )}
                              </td>
                              <td className="py-1 pr-3 text-muted">
                                {/* Step 3's measured state: what the last switch job for this path did. Which backend serves the
                                    path NOW (the shared_mounts row) has no endpoint of its own; the last job's outcome is the
                                    nearest measured answer, and it is labelled as that. */}
                                {!path ? null : j ? (
                                  <>
                                    last job <span className="font-mono">#{j.id}</span> {j.from} → {j.to}:{" "}
                                    <StatusPill tone={stateTone(j.state)}>{j.state}</StatusPill>
                                  </>
                                ) : (
                                  "no switch job yet"
                                )}
                              </td>
                              <td className="py-1 text-right">
                                {!v.started ? (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    disabled={starting === v.name}
                                    onClick={() => {
                                      setStarting(v.name);
                                      void client
                                        .apiFetch(`/glusterfs/volumes/${encodeURIComponent(v.name)}/start`, { method: "POST" })
                                        .then(() => {
                                          notify({ kind: "success", title: `Volume ${v.name} started` });
                                          void qc.invalidateQueries({ queryKey: ["gluster-volumes"] });
                                        })
                                        .catch((e: unknown) => notify({ kind: "error", title: `${v.name} did not start`, body: errText(e) }))
                                        .finally(() => setStarting(null));
                                    }}
                                  >
                                    {starting === v.name ? "Starting…" : "Start volume"}
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    disabled={!!backBlocked}
                                    title={backBlocked}
                                    onClick={() => onSwitchBack(path)}
                                  >
                                    Switch {path} back…
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </li>
                <li className="flex flex-wrap items-baseline gap-2">
                  <StatusPill tone="neutral">3</StatusPill>
                  <span>Switch each path back</span>
                  <span className="text-muted">
                    “Switch … back” fills the start form below (GlusterFS as the target); the job copies live, pauses the path's
                    writers on every node for the final copy and the swap, and keeps the JuiceFS copy.
                  </span>
                </li>
                <li className="flex flex-wrap items-baseline gap-2">
                  <StatusPill tone={running.length > 0 ? "accent" : "neutral"}>4</StatusPill>
                  <span>Watch the jobs</span>
                  <span className="text-muted">
                    {running.length > 0 ? `${running.map((j) => "#" + j.id + " at " + j.step).join(", ")} running — in the list below` : "none running — the list below"}
                  </span>
                </li>
              </ol>
            </div>
          </div>
        )}
      </StateView>
    </Section>
  );
}

/** Retire / un-retire answers: `stopped` (volumes), `this_node` (an object — what this node did), `note`. */
function RetireResult({ value }: { value: Record<string, unknown> }) {
  const stopped = value.stopped as string[] | undefined;
  const here = value.this_node as Record<string, unknown> | null | undefined;
  return (
    <div className="space-y-1">
      {stopped && <div>Volumes stopped: {stopped.length ? <span className="font-mono">{stopped.join(", ")}</span> : "none"}</div>}
      {here !== undefined && (
        <div>
          This node:{" "}
          {here && Object.keys(here).length > 0 ? (
            <span className="font-mono text-[11.5px]">
              {Object.entries(here)
                .map(([k, v]) => `${k}: ${typeof v === "string" ? v : JSON.stringify(v)}`)
                .join(" · ")}
            </span>
          ) : (
            "nothing to change (already in step)"
          )}
        </div>
      )}
      {typeof value.note === "string" && <div className="text-muted">{value.note}</div>}
    </div>
  );
}

// A path switch, started by hand. The engine migration has its own card (JuiceFS & Garage tab); a
// PATH switch had no caller but the generic operations form — and it is the way BACK from JuiceFS to
// GlusterFS that the retirement keeps open (un-retire, then switch the path back).
function StartSwitch({
  self,
  primary,
  running,
  known,
  mp,
  setMp,
  to,
  setTo,
  ret,
  vols,
}: {
  self?: string;
  primary?: string;
  running: SwitchJob[];
  known: boolean;
  mp: string;
  setMp: (v: string) => void;
  to: string;
  setTo: (v: string) => void;
  ret: RetirementQuery;
  vols: VolumesQuery;
}) {
  const { client } = useAuth();
  const path = mp.trim();
  const pathOK = path === "/mnt/cluster" || /^\/home\/[^/\s]+$/.test(path);

  // StartJob's refusals that this console can know beforehand. The rest (already served by the target,
  // the path's backend row unreadable) only the server can answer, and it says which.
  const unavailable = !known
    ? "The switch jobs have not been read, so whether one is already running is unknown."
    : running.length > 0
      ? `Switch job ${running.map((j) => "#" + j.id).join(", ")} is still running — one switch at a time, cluster-wide.`
      : !self || !primary
        ? "Which node coordinates switches (the declared primary) could not be read from the cluster state."
        : self !== primary
          ? `Only the node allowed to run cluster singletons coordinates a switch — the declared primary, ${primary}. This console is on ${self}, and the server does not relay: open ${primary}'s console.`
          : !pathOK
            ? "Enter the platform root (/mnt/cluster) or a tenant home (/home/<system user>) — the only paths the switch job moves."
            : undefined;

  const retired = ret.data?.intent;
  const vol = pathOK ? glusterVolumeFor(path) : "";
  const v = vols.data?.items?.find((x) => x.name === vol);
  // Neither of these is checked by the job's own preflight (job.go "preflight" asks only that every node
  // serves the path writably, and that JuiceFS is ready when JuiceFS is the target). Without them the job
  // fails at bulk_copy, mounting the volume for the copy — before any writer pauses, so nothing moves.
  const preflight =
    to === "glusterfs"
      ? [
          {
            ok: retired === false,
            label: "GlusterFS is not retired",
            detail:
              retired === true
                ? "it is retired cluster-wide: glusterd is stopped on every node, so the copy cannot mount the volume — un-retire it first (the steps at the top of this tab)"
                : typeof retired === "string"
                  ? retired
                  : ret.error
                    ? "the retirement state could not be read: " + errText(ret.error)
                    : "not read yet",
          },
          {
            ok: v?.started === true,
            label: vol ? `Volume ${vol} is started` : "The path's GlusterFS volume is started",
            detail: !vol
              ? "enter a path first"
              : vols.error
                ? "the volumes could not be listed here: " + errText(vols.error)
                : !vols.data
                  ? "not read — glusterd answers only once GlusterFS is un-retired"
                  : !v
                    ? `there is no volume ${vol} on this cluster — nothing to switch back to`
                    : v.started
                      ? undefined
                      : `it is ${v.status || "stopped"}; an un-retire leaves every volume stopped — start it in step 2 at the top of this tab`,
          },
        ]
      : undefined;

  return (
    <Section title="Start a path switch" hint="Coordinated by the declared primary; one at a time">
      <div className="space-y-3">
        <div className="grid gap-3 md:grid-cols-2">
          <Field
            label="Mountpoint"
            value={mp}
            onChange={setMp}
            mono
            placeholder="/mnt/cluster"
            help="The platform root, or one tenant home (/home/<system user>)."
          />
          <SelectField
            label="Move to"
            value={to}
            onChange={setTo}
            options={[
              { value: "juicefs", label: "JuiceFS", help: "The shared filesystem every path is on since the GlusterFS retirement." },
              { value: "glusterfs", label: "GlusterFS", help: "The way back: needs GlusterFS un-retired first, with the path's volume intact (bricks were kept)." },
            ]}
          />
        </div>
        <ActionCard
          title={path ? `Move ${path} to ${to === "juicefs" ? "JuiceFS" : "GlusterFS"}` : "Move a path"}
          description="Copies the path live, pauses its writers on EVERY node for the final copy, verifies every file, swaps every node and proves it (kernel filesystem type and a write), then resumes the writers. Any failure after the pause swaps every node back. The old backend's copy is kept."
          actionLabel="Start the switch"
          danger="high"
          unavailable={unavailable}
          preflight={preflight}
          confirm={{
            title: `Move ${path} to ${to === "juicefs" ? "JuiceFS" : "GlusterFS"} on every node?`,
            body: (
              <p className="text-[12.5px] leading-relaxed">
                The services that write to {path || "the path"} stop on every node for the final copy and the swap — a
                maintenance window, measured and shown on the job. Progress appears in the list above.
              </p>
            ),
            confirmLabel: "Start the switch",
          }}
          run={() => client.apiFetch<SwitchJob>("/storage/switch", { method: "POST", body: JSON.stringify({ mountpoint: path, to }) })}
          invalidates={[["storage-switch-jobs"]]}
          renderResult={(r) => {
            const j = r as SwitchJob;
            return (
              <span>
                Job <span className="font-mono">#{j.id}</span> started on {j.coordinator}: {j.from} → {j.to}, at step{" "}
                <span className="font-mono">{j.step}</span>. Follow it in the list above.
              </span>
            );
          }}
        />
      </div>
    </Section>
  );
}
